import styled from '@emotion/styled';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { CircularProgress, FormHelperText } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useLayoutTypeContext from 'framework/base/hooks/useLayoutTypeContext';
import useFrameworkTranslation from 'framework/language/useFrameworkTranslation';
import React, { useEffect, useRef, useState } from 'react';
import Select, { components } from 'react-select';

const Label = styled.label`
  right: 10px;
  pointer-events: none;
  position: absolute;
  z-index: 1;
  transition: 0.2s ease all;
  -moz-transition: 0.2s ease all;
  -webkit-transition: 0.2s ease all;
  background-color: ${(props) =>
    props.isfloating && props.theme.reactSelect.background.color};
  padding: ${(props) => props.isfloating && '0 0.25rem'};
  color: ${(props) =>
    props.isfocused
      ? props.theme.palette.primary.main
      : props.theme.textField.label.color};
  top: ${(props) => (props.isfloating ? '-9px' : 'calc(50% - 14px)')};
  font-size: ${(props) =>
    props.isfloating ? '.625rem' : props.theme.typography.h6Medium.fontSize};
  font-weight: ${(props) => props.theme.typography.h6Medium.fontWeight};
  line-height: ${(props) => !props.isfloating && '1.75rem'};
`;

function AryAutoCompleteBase({
  loadOptions,
  enableAutoFilter,
  disabled,
  label,
  style,
  maxheight,
  size = 'normal',
  error,
  helperText,
  noOptionsMessage,
  loading,
  options,
  disabledSearchText = false,
  ...others
}) {
  const { t } = useFrameworkTranslation();
  const theme = useTheme();
  const [inputValue, setInputValue] = useState();
  const layoutType = useLayoutTypeContext();
  const selectRef = useRef();

  const loadingOption = [
    {
      value: -1,
      label: (
        <CircularProgress
          style={{
            width: layoutType === 'mobile' ? '20px' : '1.25rem',
            height: layoutType === 'mobile' ? '20px' : '1.25rem'
          }}
        />
      )
    }
  ];

  function getBackgroundColor(state) {
    if (!state) return null;
    if (loading) return theme.reactSelect.background.color;
    if (state.isSelected) {
      return theme.reactSelect.selectedOption.backgroundColor;
    }
    if (state.isFocused) {
      return theme.reactSelect.hoveredOption.backgroundColor;
    }
    return theme.reactSelect.background.color;
  }

  const DropdownIndicator = (props) => {
    const { selectProps } = props;

    const iconStyle = {
      color: error ? theme.palette.error.main : theme.reactSelect.icon.color
    };

    return (
      <components.DropdownIndicator layouttype={layoutType} {...props}>
        {selectProps.menuIsOpen ? (
          <ArrowDropUp sx={iconStyle} />
        ) : (
          <ArrowDropDown sx={iconStyle} />
        )}
      </components.DropdownIndicator>
    );
  };

  const Control = ({ children, ...props }) => {
    const { isFocused, hasValue } = props;

    return (
      <components.Control {...props}>
        <Label isfloating={isFocused || hasValue} isfocused={isFocused}>
          {label}
        </Label>
        {children}
      </components.Control>
    );
  };

  useEffect(() => {
    if (disabledSearchText) {
      loadOptions?.();
    }
  }, [disabledSearchText]);

  useEffect(() => {
    if (!disabledSearchText) {
      loadOptions?.(inputValue);
    }
  }, [inputValue, disabledSearchText]);

  const handleInputChange = (newValue) => {
    setInputValue(newValue);
    return newValue;
  };

  const myComponents = () => {
    if (label) {
      return { DropdownIndicator };
    }
    if (!label) {
      return { DropdownIndicator };
    }
    return { DropdownIndicator, Control };
  };

  return (
    <>
      <Select
        noOptionsMessage={() => noOptionsMessage || t('No Option Message')}
        onInputChange={handleInputChange}
        filterOption={enableAutoFilter || disabledSearchText ? undefined : (i) => i}
        options={loading ? loadingOption : options}
        isDisabled={disabled}
        ref={selectRef}
        placeholder={label}
        components={myComponents()}
        styles={{
          control: (provided, state) => ({
            ...provided,
            background: 'inherit',
            minHeight:
              size === 'small'
                ? theme.componentSizes.filterFieldHeight
                : layoutType === 'mobile'
                ? '48px'
                : '2rem',
            maxHeight: layoutType === 'mobile' ? '48px' : '2rem',
            height: size === 'small' && theme.componentSizes.filterFieldHeight,
            boxShadow: 'none',
            borderWidth: state.isFocused ? '2px' : '1px',
            borderColor: error
              ? theme.palette.error.main
              : state.isFocused
              ? theme.palette.primary.main
              : theme.reactSelect.border.color,
            '&:hover': {
              borderColor: error
                ? theme.palette.error.main
                : !state.isFocused && theme.text.primary.color
            },
            marginBottom:
              others.openedMenuMarginBottom && state.menuIsOpen
                ? others.openedMenuMarginBottom
                : 0,
            ...style?.control
          }),
          input: (provided) => ({
            ...provided,
            color: theme.text.primary.color,
            fontSize:
              layoutType === 'laptop' || layoutType === 'tablet'
                ? '.75rem'
                : layoutType === 'desktop'
                ? '0.875rem'
                : '12px',
            fontWeight: 500,
            lineHeight:
              layoutType === 'laptop' || layoutType === 'tablet'
                ? '0.875rem'
                : layoutType === 'desktop'
                ? '1rem'
                : '14px',
            ...style?.input
          }),
          option: (provided, state) => ({
            ...provided,
            color: theme.text.primary.color,
            padding: layoutType === 'mobile' ? '0 16px' : '0 1rem',
            minHeight:
              layoutType === 'desktop'
                ? '2.375rem'
                : layoutType === 'laptop' || layoutType === 'tablet'
                ? '1.875rem'
                : '38px',
            backgroundColor: getBackgroundColor(state),
            display: 'flex',
            alignItems: 'center',
            justifyContent: loading ? 'center' : 'flex-start',
            fontSize:
              layoutType === 'desktop'
                ? '.875rem'
                : layoutType === 'laptop' || layoutType === 'tablet'
                ? '.75rem'
                : '12px',
            lineHeight:
              layoutType === 'laptop' || layoutType === 'tablet'
                ? '1.875rem'
                : layoutType === 'desktop'
                ? '2.375rem'
                : '38px',
            '&:hover': {
              backgroundColor: loading
                ? theme.reactSelect.background.color
                : theme.reactSelect.hoveredOption.backgroundColor
            },
            ...style?.option
          }),
          noOptionsMessage: (provided) => ({
            ...provided,
            padding: layoutType === 'desktop' ? '.5rem .75rem' : 0,
            ...style?.noOptionsMessage
          }),
          clearIndicator: (provided) => ({
            ...provided,
            padding: 0,
            ...style?.noOptionsMessage
          }),
          placeholder: (provided) => ({
            ...provided,
            textWrap: 'nowrap',
            color: error
              ? theme.palette.error.main
              : theme.reactSelect.placeholder.color,
            fontSize:
              layoutType === 'laptop' || layoutType === 'tablet'
                ? '.75rem'
                : layoutType === 'desktop'
                ? '.875rem'
                : '12px',
            fontWeight: 500,
            lineHeight:
              layoutType === 'laptop' || layoutType === 'tablet'
                ? '.875rem'
                : layoutType === 'desktop'
                ? '1rem'
                : '14px',
            ...style?.placeholder
          }),
          singleValue: (provided) => ({
            ...provided,
            color: theme.text.primary.color,
            fontSize:
              layoutType === 'laptop' || layoutType === 'tablet'
                ? '.75rem'
                : layoutType === 'desktop'
                ? '.875rem'
                : '12px',
            fontWeight: 500,
            lineHeight:
              layoutType === 'laptop' || layoutType === 'tablet'
                ? '.875rem'
                : layoutType === 'desktop'
                ? '1rem'
                : '14px',
            ...style?.singleValue
          }),
          menu: (provided) => ({
            ...provided,
            background: theme.reactSelect.background.color,
            marginTop: layoutType === 'mobile' ? '5px' : '.313rem',
            padding: layoutType === 'mobile' ? '4px 0' : '.25rem 0',
            borderRadius: layoutType === 'mobile' ? '4px' : '.25rem',
            boxShadow:
              '0px 4px 14px 2px rgba(0, 0, 0, 0.12), 0px 4px 4px rgba(0, 0, 0, 0.25), 0px 4px 10px 1px rgba(0, 0, 0, 0.14)',
            zIndex: 2,
            ...style?.menu
          }),
          menuList: (provided) => ({
            ...provided,
            maxHeight:
              maxheight || (layoutType === 'mobile' ? '230px' : '14.375rem'),
            ...style?.menuList
          }),
          dropdownIndicator: (provided) => ({
            ...provided,
            padding:
              layoutType === 'laptop' || layoutType === 'tablet'
                ? '0 .25rem'
                : layoutType === 'desktop'
                ? '0 .3rem'
                : '0 8px',
            ...style?.dropdownIndicator
          }),
          indicatorSeparator: () => null,
          valueContainer: (provided) => ({
            ...provided,
            paddingRight: '0.25rem',
            paddingLeft: '0.25rem'
          })
        }}
        {...others}
      />
      {!!helperText && (
        <FormHelperText error={error}>{helperText}</FormHelperText>
      )}
    </>
  );
}

export default AryAutoCompleteBase;
